/**
 * Settings tab for the Company Profile page (CR-6247).
 *
 * Currency, Timezone and Date Format for this one company, plus the Portal Mode
 * it runs in. Reuses SiteRegionalRow from Admin Setup > Regional Settings so the
 * inherit-vs-override behaviour CR-6256 defined stays in one place; this tab is
 * the single-company view of it, that page remains the bulk view and the only
 * place group defaults are set.
 *
 * The values in force lead the tab. CR-6256 shows the three dropdowns only for
 * an overriding site — on the Regional Settings page the inherited values are
 * stated once at the top, but this tab has no such header, so an inheriting
 * company would otherwise say nothing at all about its own regional settings.
 *
 * Portal Mode is read-only here: CR-6650 made it a Valuechain contract decision
 * owned by Platform Admin > Platform Config, so the client neither sets it nor
 * sees it — the row is shown to Helix users only, matching the org chart badge.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Globe, Loader2, AlertCircle, Building2, Banknote, Clock, Calendar,
  MonitorSmartphone, type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { SiteRegionalRow } from '../../components/admin/settings/SiteRegionalRow';
import { preferencesAPI } from '../../lib/api';
import type {
  SiteRegionalSettings,
  PreferencesChoices,
  Company,
} from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useAuth } from '../../contexts/AuthContext';

interface SettingsTabProps {
  company: Company;
  /** Re-read the company so the values in force follow a save. */
  onCompanyRefresh: () => void | Promise<void>;
}

const PORTAL_MODE_LABELS: Record<string, string> = {
  customer: 'Customer Portal',
  supplier: 'Supplier Portal',
  both: 'Customer and Supplier Portal',
};

/** One resolved value in the "currently in force" strip. */
function InForceValue({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-words">{value || 'Not set'}</p>
      </div>
    </div>
  );
}

/**
 * Regional settings for a single company, and the portal mode it runs in.
 *
 * @param company - the company being viewed
 * @param onCompanyRefresh - re-reads the company after a settings change
 */
export function SettingsTab({ company, onCompanyRefresh }: SettingsTabProps) {
  const companyId = company.id;
  // Regional settings are configured for customer companies only.
  const isCustomerCompany = company.company_type === 'customer';
  const { user } = useAuth();
  // CR-6650: portal mode is ours to state, not the client's to see.
  const isHelixUser = !!(user?.is_superuser || user?.is_staff || user?.company_type === 'helix');
  const inForce = company.effective_regional_settings;
  const inherits = company.uses_group_regional_defaults !== false;
  const [site, setSite] = useState<SiteRegionalSettings | null>(null);
  const [choices, setChoices] = useState<PreferencesChoices | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Partial<SiteRegionalSettings>>({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    if (!isCustomerCompany) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // The dropdown choices are supporting context — a failure there must not
      // hide the site's own values, so only the site fetch is fatal. The group
      // defaults are no longer needed here: an inheriting site shows the two
      // mode buttons only, and Override opens on the materialised values.
      const [siteRes, choicesRes] = await Promise.all([
        preferencesAPI.getSiteRegionalSettingsById(companyId),
        preferencesAPI.getPreferencesChoices().catch(() => null),
      ]);
      setSite(siteRes.data);
      setChoices(choicesRes?.data ?? null);
      setEdits({});
    } catch (err: unknown) {
      console.error('Failed to load company regional settings', err);
      setSite(null);
      setError(getApiErrorMessage(err, 'Failed to load regional settings for this company.'));
    } finally {
      setLoading(false);
    }
  }, [companyId, isCustomerCompany]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSettingChange = (
    _siteId: number,
    field: string,
    value: string | boolean | null,
  ) => {
    setEdits((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!site || Object.keys(edits).length === 0) return;
    setSaving(true);
    try {
      await preferencesAPI.updateSiteRegionalSettings(site.id, edits);
      toast.success('Regional settings saved');
      await load();
      await onCompanyRefresh();
    } catch (err: unknown) {
      console.error('Failed to save company regional settings', err);
      toast.error(getApiErrorMessage(err, 'Failed to save regional settings'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Persisted on click rather than behind Save — same choice the Regional
   * Settings page makes, since leaving it unsaved would show a company as
   * inheriting while it still overrides.
   */
  const handleInheritanceChange = async (siteId: number, usesGroupDefaults: boolean) => {
    setResetting(true);
    try {
      await preferencesAPI.updateSiteRegionalSettings(siteId, {
        uses_group_regional_defaults: usesGroupDefaults,
      });
      toast.success(
        usesGroupDefaults
          ? 'Now inheriting the group defaults'
          : 'Now overriding the group defaults',
      );
      await load();
      await onCompanyRefresh();
    } catch (err: unknown) {
      console.error('Failed to update inheritance', err);
      toast.error(getApiErrorMessage(err, 'Failed to update regional settings'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Regional Settings
        </CardTitle>
        <CardDescription>
          Currency, timezone and date format for this company. It follows the group
          defaults until you choose Override Default Settings, which gives it values
          of its own.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* What the company actually runs on today. An inheriting company has
            dormant columns of its own, so this reads the resolved values the
            API sends rather than the row's stored ones. */}
        {isCustomerCompany && inForce && (
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-sm font-medium">Currently in force</p>
              <Badge variant="outline">
                {inherits
                  ? `Inherited from ${company.group_name || 'the group'}`
                  : 'Set on this company'}
              </Badge>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <InForceValue icon={Banknote} label="Currency" value={inForce.currency} />
              <InForceValue icon={Clock} label="Timezone" value={inForce.timezone} />
              <InForceValue icon={Calendar} label="Date Format" value={inForce.date_format} />
            </div>
          </div>
        )}

        {!isCustomerCompany ? (
          <div className="text-center py-8 text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Regional settings are configured for customer companies only.</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-start justify-center gap-2 py-8 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        ) : !site ? (
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No regional settings are configured for this company.</p>
          </div>
        ) : (
          <SiteRegionalRow
            site={site}
            choices={choices}
            edits={edits}
            saving={saving}
            togglingInheritance={resetting}
            onSettingChange={handleSettingChange}
            onSave={handleSave}
            onInheritanceChange={handleInheritanceChange}
          />
        )}

        {/* CR-6650: portal mode is set by Valuechain in Platform Admin, so it
            is stated here rather than edited, and only for our own users. */}
        {isHelixUser && (
          <div className="border-t pt-4">
            <div className="flex items-start gap-3">
              <MonitorSmartphone className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Portal Mode</p>
                <p className="text-sm text-muted-foreground">
                  {company.portal_mode
                    ? PORTAL_MODE_LABELS[company.portal_mode] || company.portal_mode
                    : 'Not set'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Set in Platform Admin &gt; Platform Config. Not visible to the customer.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SettingsTab;
